
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store';
import { Urgency } from '../types';
import { categorizeRequestLocal } from '../services/localService';
import { Camera, Video, Mic, MapPin, Loader2, X, ChevronRight, ChevronLeft, Hammer } from 'lucide-react';

const CreateRequest: React.FC = () => {
  const [text, setText] = useState('');
  const [urgency, setUrgency] = useState<Urgency>(Urgency.TODAY);
  const [image, setImage] = useState<string | null>(null);
  const [location, setLocation] = useState<{lat: number, lng: number, address: string} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { currentUser, addRequest } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            address: "Current Location (Detected)"
          });
        },
        () => console.log("Location access denied")
      );
    }
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text || !currentUser) return;

    setIsProcessing(true);
    
    // Simulate a brief local processing delay for better UX
    setTimeout(() => {
      try {
        const result = categorizeRequestLocal(text);
        const newReq = addRequest({
          requesterId: currentUser.id,
          requesterName: currentUser.name,
          text,
          mediaUrl: image || undefined,
          category: result.category,
          urgency,
          estimatedPrice: result.estimatedPrice,
          location: location || { lat: 0, lng: 0, address: "Global / Remote" }
        });
        navigate(`/request/${newReq.id}`);
      } catch (error) {
        console.error(error);
      } finally {
        setIsProcessing(false);
      }
    }, 800);
  };

  return (
    <div className="max-w-xl mx-auto pb-24 px-4 reveal">
      <div className="flex items-center gap-5 mb-10">
        <button 
          onClick={() => navigate(-1)} 
          className="p-3 bg-white border border-stone-200 hover:bg-stone-50 rounded-xl text-stone-600 transition-all shadow-sm hover:text-amber-800"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h2 className="text-4xl font-black text-[#2d1b0f] tracking-tight font-serif">Commission</h2>
          <p className="text-stone-400 text-[10px] font-black uppercase tracking-[0.2em]">Craft your master request</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Media & Description */}
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-stone-100 space-y-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 wood-grain opacity-[0.03] pointer-events-none"></div>
          
          <div className="space-y-4">
            <label className="text-[10px] font-black text-amber-800 uppercase tracking-[0.3em] flex items-center gap-2">
              <span className="w-2 h-px bg-amber-700"></span>
              The Project Scope
            </label>
            <textarea
              required
              className="w-full h-48 p-6 bg-stone-50 border border-stone-200 rounded-[2rem] focus:ring-4 focus:ring-amber-50 focus:border-amber-300 outline-none transition-all resize-none text-stone-800 font-medium placeholder:text-stone-300 shadow-inner"
              placeholder="Describe your need... e.g., 'Restore a split mahogany chair leg' or 'Fix a jammed antique lock'."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          <div className="space-y-4">
            <label className="text-[10px] font-black text-amber-800 uppercase tracking-[0.3em] flex items-center gap-2">
              <span className="w-2 h-px bg-amber-700"></span>
              Visual Evidence
            </label>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-32 h-32 border-2 border-dashed border-stone-200 rounded-3xl flex flex-col items-center justify-center text-stone-300 hover:border-amber-400 hover:bg-amber-50/50 hover:text-amber-800 transition-all group shadow-sm bg-white"
              >
                {image ? (
                  <div className="relative w-full h-full group">
                    <img src={image} className="w-full h-full object-cover rounded-[1.4rem]" alt="Preview" />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-[1.4rem]">
                       <X className="text-white" size={24} />
                    </div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setImage(null); }}
                      className="absolute -top-3 -right-3 bg-rose-600 text-white p-1.5 rounded-full shadow-xl border-2 border-white"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Camera size={32} className="mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-[9px] font-black uppercase tracking-widest">Add Photo</span>
                  </>
                )}
              </button>
              
              <button type="button" className="flex-shrink-0 w-32 h-32 border-2 border-dashed border-stone-50 rounded-3xl flex flex-col items-center justify-center text-stone-200 cursor-not-allowed bg-stone-50/30">
                <Video size={32} className="mb-2 opacity-50" />
                <span className="text-[9px] font-black uppercase tracking-widest opacity-50">Video</span>
              </button>
              
              <button type="button" className="flex-shrink-0 w-32 h-32 border-2 border-dashed border-stone-50 rounded-3xl flex flex-col items-center justify-center text-stone-200 cursor-not-allowed bg-stone-50/30">
                <Mic size={32} className="mb-2 opacity-50" />
                <span className="text-[9px] font-black uppercase tracking-widest opacity-50">Audio</span>
              </button>
              
              <input type="file" ref={fileInputRef} onChange={handleImageChange} className="hidden" accept="image/*" />
            </div>
          </div>
        </div>

        {/* Logistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-stone-100">
            <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] mb-6 block">Timeline</label>
            <div className="space-y-3">
              {[Urgency.ASAP, Urgency.TODAY, Urgency.THIS_WEEK].map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUrgency(u)}
                  className={`w-full py-4 px-5 rounded-xl text-xs font-black uppercase tracking-[0.2em] text-left transition-all flex items-center justify-between group border-b-2 ${
                    urgency === u 
                      ? 'bg-amber-800 text-white border-black/20 shadow-lg' 
                      : 'bg-[#faf7f2] text-stone-600 border-stone-200 hover:border-amber-400 hover:text-amber-800'
                  }`}
                >
                  {u}
                  <ChevronRight size={18} className={urgency === u ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 transition-opacity'} />
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-stone-100">
            <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] mb-6 block">The Site</label>
            <div className="bg-[#faf7f2] p-6 rounded-2xl flex flex-col items-center justify-center text-center border-2 border-dashed border-stone-200 min-h-[160px] group transition-all hover:border-amber-400">
              <MapPin size={32} className="text-amber-700 mb-3 group-hover:animate-bounce" />
              <p className="text-[11px] font-black text-stone-900 uppercase tracking-tight leading-tight">{location ? location.address : "Seeking Coordinates..."}</p>
              <button type="button" className="mt-4 text-[9px] font-black text-amber-800 uppercase tracking-[0.3em] hover:text-amber-600 transition-colors">Adjust Location</button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isProcessing || !text}
          className="w-full bg-amber-800 text-amber-50 py-6 rounded-2xl font-black text-xl flex items-center justify-center gap-4 hover:bg-amber-900 transition-all disabled:bg-stone-200 shadow-2xl border-b-8 border-black/30 active:border-b-0 active:translate-y-2"
        >
          {isProcessing ? (
            <>
              <Loader2 className="animate-spin" size={28} />
              <span className="font-serif italic text-lg">Appraising Craft...</span>
            </>
          ) : (
            <>
              Commission Request
              <Hammer size={24} className="rotate-12" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default CreateRequest;
